import React, { useState, useEffect, useRef } from 'react';
import {
  FaSync, FaChartLine, FaMoneyBillWave, FaArrowUp,
  FaArrowDown, FaExchangeAlt, FaWallet, FaCalendarAlt,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import dayjs from 'dayjs';

/* ─── Renk paleti — kasa bazında sabit renkler ───────────────────── */
const KASA_RENK_MAP = {
  FAYNPAY:   '#f27127',
  NEOPAYS:   '#06b6d4',
  ULTRAPAY:  '#8b5cf6',
  FULGUR:    '#f59e0b',
  ANINDA:    '#10b981',
  MİNİ:      '#ec4899',
  ENHİZLİ:   '#3b82f6',
  DIS_KASA:  '#94a3b8',
};
const FALLBACK_RENKLER = ['#14b8a6','#a3e635','#fb923c','#e879f9','#38bdf8','#fde68a'];
const kasaRenk = (() => {
  const extra = {};
  let i = 0;
  return (k) => KASA_RENK_MAP[k?.toUpperCase()] ?? (extra[k] = extra[k] ?? FALLBACK_RENKLER[i++ % FALLBACK_RENKLER.length]);
})();

/* ─── Sayı formatı ────────────────────────────────────────────────── */
const fmt = (n) => new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n ?? 0);

const fmtShort = (n) => {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(Math.round(n));
};

/* ─── Tooltip hook ────────────────────────────────────────────────── */
const useTooltip = () => {
  const [tip, setTip] = useState(null);
  return {
    tip,
    show: (e, content) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setTip({ x: rect.left + rect.width / 2, y: rect.top - 8, content });
    },
    hide: () => setTip(null),
  };
};

/* ══════════════════════════════════════════════════════════════════ */
const FinansDashboard = () => {
  const bugun = dayjs();
  const [ay, setAy]   = useState(bugun.month() + 1);
  const [yil, setYil] = useState(bugun.year());
  const [loading, setLoading]           = useState(true);
  const [genelDurum, setGenelDurum]     = useState(null);
  const [kasaOzet, setKasaOzet]         = useState([]);
  const [canliOzet, setCanliOzet]       = useState([]);
  const [canliHata,  setCanliHata]       = useState(false);
  const [gunlukOzet, setGunlukOzet]     = useState([]);
  const [kasaGunluk, setKasaGunluk]     = useState([]);
  const [doviz, setDoviz]               = useState(null);
  const [activeGun, setActiveGun]       = useState(null);
  const [bekleyenFatura, setBekleyenFatura] = useState({ totalTL: 0, totalEUR: 0, count: 0, invoices: [] });
  const tooltip = useTooltip();

  const AY_ADLARI = ['','Ocak','Şubat','Mart','Nisan','Mayıs','Haziran',
    'Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

  const fetchDoviz = async () => {
    try { const r = await axiosInstance.get('/doviz/usd-try'); setDoviz(r.data); } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gdR, gunR, kasaR, yonR, canliR, faturaR] = await Promise.all([
        axiosInstance.get(`/rapor/aylik?ay=${ay}&yil=${yil}`),
        axiosInstance.get(`/rapor/gunluk?ay=${ay}&yil=${yil}`),
        axiosInstance.get(`/rapor/kasalar?ay=${ay}&yil=${yil}`),
        axiosInstance.get(`/rapor/gunluk-yontem?ay=${ay}&yil=${yil}`),
        axiosInstance.get(`/rapor/canli?ay=${ay}&yil=${yil}`).catch(() => null),
        axiosInstance.get('/commission/invoices/pending').catch(() => null),
      ]);
      setGenelDurum(gdR.data);
      setGunlukOzet(gunR.data);
      setKasaOzet(kasaR.data);
      setKasaGunluk(yonR.data);
      if (faturaR?.data) setBekleyenFatura(faturaR.data);
      else setBekleyenFatura({ totalTL: 0, totalEUR: 0, count: 0, invoices: [] });
      if (canliR && canliR.data?.rapor?.length > 0) {
        setCanliOzet(canliR.data.rapor);
        setCanliHata(false);
      } else {
        setCanliOzet([]);
        setCanliHata(true);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); fetchDoviz(); }, [ay, yil]);

  const ozet = genelDurum ?? {};
  // DIS_KASA + toplam için canli kasa map'i
  const canliMap = Object.fromEntries((canliOzet).map(k => [k.kasa, k]));
  const kalanPos = (ozet.kalan ?? 0) >= 0;

  /* tüm kasaları topla */
  const tumKasalar = [...new Set(kasaGunluk.flatMap(g => Object.keys(g.kasalar)))].sort();
  const maxKasa    = Math.max(...kasaGunluk.map(g => g.toplam), 1);
  const yAxisMax   = Math.ceil(maxKasa / 1e5) * 1e5 || 1;
  const ySteps     = 5;
  const yLabels    = Array.from({ length: ySteps + 1 }, (_, i) =>
    Math.round((yAxisMax / ySteps) * (ySteps - i))
  );

  return (
    <div style={S.page}>
      {/* ambient blobs */}
      <div style={{ ...S.blob, top: -200, right: -100, background: 'radial-gradient(circle, rgba(242,113,39,0.06) 0%, transparent 60%)' }} />
      <div style={{ ...S.blob, bottom: -150, left: -80, background: 'radial-gradient(circle, rgba(6,182,212,0.04) 0%, transparent 60%)' }} />

      {/* ── HEADER ────────────────────────────────── */}
      <div style={S.header}>
        <div>
          <h2 style={S.headerTitle}>
            <FaChartLine style={{ color: '#f27127', marginRight: 10 }} />
            Genel Durum
          </h2>
          <p style={S.headerSub}>{AY_ADLARI[ay]} {yil} • Finansal Özet</p>
          {doviz && (
            <div style={S.dovizPill}>
              <span style={S.dovizLbl}>USD/TRY</span>
              <span style={S.dovizVal}>{doviz.kur?.toFixed(4)}</span>
              <span style={S.dovizMeta}>TCMB • {doviz.kalanDakika}dk</span>
            </div>
          )}
        </div>
        <div style={S.headerRight}>
          <select style={S.sel} value={ay} onChange={e => setAy(Number(e.target.value))}>
            {AY_ADLARI.slice(1).map((a, i) => <option key={i+1} value={i+1}>{a}</option>)}
          </select>
          <select style={S.sel} value={yil} onChange={e => setYil(Number(e.target.value))}>
            {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={S.refreshBtn} onClick={() => { fetchData(); fetchDoviz(); }}>
            <FaSync style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          </button>
        </div>
      </div>

      {loading ? (
        <div style={S.loadingWrap}>
          <div style={S.spinner} />
          <p style={{ color: '#475569', marginTop: 16, fontSize: '0.85rem' }}>Yükleniyor...</p>
        </div>
      ) : (<>

        {/* ── BEKLEYEN FATURA UYARISI ─────────── */}
        {bekleyenFatura.count > 0 && (
          <div style={{
            background: 'rgba(249,115,22,0.05)', border: '1px solid rgba(249,115,22,0.15)',
            borderRadius: 14, padding: '12px 18px', marginBottom: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            animation: 'fadeUp 0.3s ease both',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(249,115,22,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaFileInvoiceDollar style={{ color: '#f97316', fontSize: '0.85rem' }} />
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fb923c' }}>Bekleyen Fatura ({bekleyenFatura.count})</div>
                <div style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: 1 }}>
                  {bekleyenFatura.invoices?.map(inv => inv.period).join(', ')}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1rem', fontWeight: 800, color: '#f97316', fontVariantNumeric: 'tabular-nums' }}>{fmt(bekleyenFatura.totalTL)} ₺</div>
              <div style={{ fontSize: '0.55rem', color: '#94a3b8' }}>{fmt(bekleyenFatura.totalEUR)} EUR</div>
            </div>
          </div>
        )}

        {/* ── STAT KARTLARI ─────────────────────── */}
        <div style={S.statGrid}>
          {[
            { icon: <FaArrowUp />,      label: 'Toplam Yatırım',  val: ozet.yatirim,   color: '#4ade80' },
            { icon: <FaArrowDown />,    label: 'Toplam Çekim',    val: ozet.cekim,     color: '#f87171' },
            { icon: <FaExchangeAlt />,  label: 'Komisyon',        val: ozet.komisyon,  color: '#fbbf24' },
            { icon: <FaMoneyBillWave />,label: 'Ödeme / Masraf',  val: ozet.odeme,     color: '#93c5fd' },
            { icon: <FaWallet />,       label: 'Marketing',       val: ozet.marketing, color: '#94a3b8' },
            { icon: <FaChartLine />,    label: 'Kalan',           val: ozet.kalan,     color: kalanPos ? '#4ade80' : '#f87171', highlight: true },
          ].map(({ icon, label, val, color, highlight }, i) => (
            <div key={i} style={{
              ...S.statCard,
              ...(highlight ? { border: `1px solid ${color}30`, boxShadow: `0 0 28px ${color}12` } : {}),
              animationDelay: `${i * 60}ms`,
            }}>
              <div style={{ ...S.statIcon, background: `${color}12`, color }}>{icon}</div>
              <div>
                <p style={S.statLabel}>{label}</p>
                <p style={{ ...S.statValue, color, fontVariantNumeric: 'tabular-nums' }}>{fmt(val)}</p>
                <p style={S.statSub}>TL</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── KASA TABLOSU ──────────────────────── */}
        <div style={S.panel}>
          <div style={S.panelHead}>
            <h3 style={S.panelTitle}>Kasa Bazında Özet</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {canliOzet.length > 0
                ? <span style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 6, padding: '2px 9px', fontSize: '0.68rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'pulse 2s infinite' }} />
                    CANLI
                  </span>
                : <span style={{ background: 'rgba(100,116,139,0.1)', color: '#64748b', border: '1px solid rgba(100,116,139,0.2)', borderRadius: 6, padding: '2px 9px', fontSize: '0.68rem', fontWeight: 700 }}>GÜN SONU</span>
              }
              <span style={S.panelBadge}>{kasaOzet.length} kasa</span>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['KASA','DEVİR','DEVİR TL','YATIRIM','ÇEKİM','KOMİSYON','ÖDEME','TAHSİLAT','VİRMAN NET','GÜN SONU KASA'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {kasaOzet.map((k, i) => (
                  <tr key={i} style={S.tr}>
                    <td style={{ ...S.td, fontWeight: 700, color: '#f1f5f9' }}>{k.kasa}</td>
                    <td style={S.td}>
                      {k.dovizKuru
                        ? <span title={`${fmt(k.devirKasasi)} $ × ${k.dovizKuru}`} style={{ cursor: 'help' }}>{fmt(k.devirKasasi)} $</span>
                        : <span style={{ color: '#475569' }}>{fmt(k.devirKasasi)}</span>}
                    </td>
                    <td style={{ ...S.td, color: k.dovizKuru ? '#fbbf24' : '#475569' }}>
                      {fmt(k.devirTL)}
                      {k.dovizKuru && <span style={{ color: '#334155', fontSize: '0.65rem', marginLeft: 4 }}>@{k.dovizKuru}</span>}
                    </td>
                    <td style={{ ...S.td, color: '#4ade80' }}>{fmt(k.yatirim)}</td>
                    <td style={{ ...S.td, color: '#f87171' }}>{fmt(k.cekim)}</td>
                    <td style={{ ...S.td, color: '#fbbf24' }}>{fmt(k.komisyon)}</td>
                    <td style={{ ...S.td, color: '#93c5fd' }}>{fmt(k.odeme)}</td>
                    <td style={{ ...S.td, color: '#818cf8' }}>{fmt(k.tahsilat)}</td>
                    <td style={{ ...S.td, fontWeight: 600,
                      color: (k.virmanNet??0)>0 ? '#4ade80' : (k.virmanNet??0)<0 ? '#f87171' : '#475569' }}>
                      {(k.virmanNet??0)>0 ? '+':''}{fmt(k.virmanNet)}
                    </td>
                    {(() => {
                      const isDis   = k.kasa === 'DIS_KASA';
                      const canliVal = isDis ? canliMap['DIS_KASA']?.canliKasa : undefined;
                      const val      = canliVal ?? k.guncelKasa;
                      return (
                        <td style={{ ...S.td, fontWeight: 800, color: val >= 0 ? '#4ade80' : '#f87171' }}>
                          {fmt(val)}
                          {isDis && canliVal != null && (
                            <span style={{ color: '#10b981', fontSize: '0.6rem', marginLeft: 5, fontWeight: 600 }}>canlı</span>
                          )}
                        </td>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={S.tfootRow}>
                  <td style={{ ...S.td, fontWeight: 800, color: '#f27127' }}>TOPLAM</td>
                  <td style={S.td}>—</td>
                  <td style={{ ...S.td, color: '#fbbf24', fontWeight: 700 }}>{fmt(kasaOzet.reduce((s,k)=>s+(k.devirTL??0),0))}</td>
                  <td style={{ ...S.td, color: '#4ade80', fontWeight: 700 }}>{fmt(kasaOzet.reduce((s,k)=>s+(k.yatirim??0),0))}</td>
                  <td style={{ ...S.td, color: '#f87171', fontWeight: 700 }}>{fmt(kasaOzet.reduce((s,k)=>s+(k.cekim??0),0))}</td>
                  <td style={{ ...S.td, color: '#fbbf24', fontWeight: 700 }}>{fmt(kasaOzet.reduce((s,k)=>s+(k.komisyon??0),0))}</td>
                  <td style={{ ...S.td, color: '#93c5fd', fontWeight: 700 }}>{fmt(kasaOzet.reduce((s,k)=>s+(k.odeme??0),0))}</td>
                  <td style={{ ...S.td, color: '#818cf8', fontWeight: 700 }}>{fmt(kasaOzet.reduce((s,k)=>s+(k.tahsilat??0),0))}</td>
                  <td style={{ ...S.td, fontWeight: 700 }}>{fmt(kasaOzet.reduce((s,k)=>s+(k.virmanNet??0),0))}</td>
                  {(() => {
                    const canliAktif = canliOzet.length > 0;
                    const t = kasaOzet.reduce((s, k) => {
                      const val = canliMap[k.kasa]?.canliKasa ?? k.guncelKasa;
                      return s + (val ?? 0);
                    }, 0);
                    return (
                      <td style={{ ...S.td, fontWeight: 900, color: t>=0?'#4ade80':'#f87171' }}>
                        {fmt(t)}
                        {canliAktif && (
                          <span style={{ color: '#10b981', fontSize: '0.6rem', marginLeft: 5, fontWeight: 600 }}>● canlı</span>
                        )}
                        {!canliAktif && (
                          <span style={{ color: '#334155', fontSize: '0.6rem', marginLeft: 5, fontWeight: 400 }}>gün sonu</span>
                        )}
                      </td>
                    );
                  })()}
                </tr>
                {/* FATURA SONRASI TOPLAM */}
                {bekleyenFatura.count > 0 && (
                  <tr style={{ ...S.tfootRow, borderTop: '1px solid rgba(249,115,22,0.15)' }}>
                    <td style={{ ...S.td, fontWeight: 700, color: '#f97316', fontSize: '0.72rem' }}>FATURA SONRASI</td>
                    <td style={S.td}>—</td>
                    <td style={S.td}>—</td>
                    <td style={S.td}>—</td>
                    <td style={S.td}>—</td>
                    <td style={S.td}>—</td>
                    <td style={S.td}>—</td>
                    <td style={S.td}>—</td>
                    <td style={{ ...S.td, color: '#f97316', fontWeight: 600, fontSize: '0.68rem' }}>-{fmt(bekleyenFatura.totalTL)}</td>
                    {(() => {
                      const canliAktif = canliOzet.length > 0;
                      const toplamKasa = kasaOzet.reduce((s, k) => {
                        const val = canliMap[k.kasa]?.canliKasa ?? k.guncelKasa;
                        return s + (val ?? 0);
                      }, 0);
                      const faturaSonrasi = toplamKasa - bekleyenFatura.totalTL;
                      return (
                        <td style={{ ...S.td, fontWeight: 900, color: faturaSonrasi>=0?'#f97316':'#f87171' }}>
                          {fmt(faturaSonrasi)}
                          <span style={{ color: '#f97316', fontSize: '0.55rem', marginLeft: 4, fontWeight: 500 }}>fatura sonrası</span>
                        </td>
                      );
                    })()}
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>

        {/* ── STACKED BAR CHART ─────────────────── */}
        <div style={S.panel}>
          <div style={S.panelHead}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaCalendarAlt style={{ color: '#f27127' }} />
              <h3 style={S.panelTitle}>Günlük Yatırım Dağılımı</h3>
            </div>
            {/* Legend */}
            <div style={S.legend}>
              {tumKasalar.map(k => (
                <div key={k} style={S.legendItem}>
                  <span style={{ ...S.legendDot, background: kasaRenk(k) }} />
                  <span style={S.legendLbl}>{k}</span>
                </div>
              ))}
            </div>
          </div>

          {kasaGunluk.length === 0 ? (
            <p style={{ color: '#334155', textAlign: 'center', padding: 40 }}>Veri yok</p>
          ) : (
            <LineChart
              data={kasaGunluk}
              kasalar={tumKasalar}
              yAxisMax={yAxisMax}
              yLabels={yLabels}
              ySteps={ySteps}
              activeGun={activeGun}
              setActiveGun={setActiveGun}
              fmt={fmt}
              fmtShort={fmtShort}
              kasaRenk={kasaRenk}
            />
          )}
        </div>

      </>)}

      {/* Floating tooltip */}
      {tooltip.tip && (
        <div style={{ ...S.tooltip, left: tooltip.tip.x, top: tooltip.tip.y }}>
          {tooltip.tip.content.split('\n').map((l, i) => (
            <div key={i} style={{ color: l.startsWith('─') ? '#1e293b' : l.startsWith('Toplam') ? '#f27127' : l.match(/^\d/) ? '#e2e8f0' : '#94a3b8', fontSize: l.startsWith('Toplam') || l.match(/^\d/) ? '0.85rem' : '0.75rem', fontWeight: l.match(/^\d/) ? 700 : 400 }}>{l}</div>
          ))}
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
        tbody tr:hover td { background: rgba(255,255,255,0.018) !important; }
        select option { background: #0f1115; }
        input::placeholder { color: #1e293b; }
      `}</style>
    </div>
  );
};

/* ══════════ STYLES ══════════════════════════════════════════════════ */
const S = {
  page: {
    padding: '28px 28px',
    minHeight: '100vh',
    backgroundColor: '#07080b',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: '#e2e8f0',
    position: 'relative',
    overflow: 'hidden',
  },
  blob: { position: 'fixed', width: 600, height: 600, pointerEvents: 'none', zIndex: 0 },

  /* header */
  header: {
    position: 'relative', zIndex: 1,
    background: 'linear-gradient(105deg, #13151a 0%, #1a1d24 100%)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 18, padding: '22px 28px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 22, boxShadow: '0 12px 40px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
    flexWrap: 'wrap', gap: 14,
  },
  headerTitle: { color: '#fff', fontSize: '1.45rem', fontWeight: 800, display: 'flex', alignItems: 'center', margin: 0 },
  headerSub:   { color: '#475569', fontSize: '0.82rem', margin: '5px 0 0' },
  headerRight: { display: 'flex', gap: 9, alignItems: 'center' },
  sel: {
    background: '#0d0f14', border: '1px solid #1e2530',
    borderRadius: 10, color: '#94a3b8', padding: '9px 13px',
    fontSize: '0.82rem', cursor: 'pointer',
  },
  refreshBtn: {
    background: 'rgba(242,113,39,0.1)', border: '1px solid rgba(242,113,39,0.2)',
    color: '#f27127', borderRadius: 10, width: 40, height: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  },
  dovizPill: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.15)',
    borderRadius: 8, padding: '5px 11px', marginTop: 8,
  },
  dovizLbl:  { color: '#64748b', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' },
  dovizVal:  { color: '#fbbf24', fontSize: '0.95rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  dovizMeta: { color: '#334155', fontSize: '0.62rem' },

  /* loading */
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 100, zIndex: 1, position: 'relative' },
  spinner: { width: 38, height: 38, borderRadius: '50%', border: '3px solid #1a1d24', borderTopColor: '#f27127', animation: 'spin 0.8s linear infinite' },

  /* stat grid */
  statGrid: {
    position: 'relative', zIndex: 1,
    display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 12, marginBottom: 20,
  },
  statCard: {
    background: 'linear-gradient(135deg, rgba(19,21,26,0.95), rgba(14,16,20,0.98))',
    border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16,
    padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    animation: 'fadeUp 0.5s ease both',
  },
  statIcon:  { width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem', flexShrink: 0 },
  statLabel: { color: '#475569', fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 4px' },
  statValue: { fontSize: '1.1rem', fontWeight: 800, margin: '0 0 2px' },
  statSub:   { color: '#1e293b', fontSize: '0.65rem', margin: 0 },

  /* panel */
  panel: {
    position: 'relative', zIndex: 1,
    background: 'linear-gradient(135deg, rgba(19,21,26,0.95), rgba(14,16,20,0.98))',
    border: '1px solid rgba(255,255,255,0.05)', borderRadius: 18,
    padding: '22px 24px', marginBottom: 20,
    boxShadow: '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.03)',
  },
  panelHead:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 },
  panelTitle: { color: '#e2e8f0', fontSize: '0.95rem', fontWeight: 700, margin: 0 },
  panelBadge: { background: 'rgba(242,113,39,0.1)', color: '#f27127', border: '1px solid rgba(242,113,39,0.2)', borderRadius: 6, padding: '2px 9px', fontSize: '0.72rem', fontWeight: 700 },

  /* table */
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '9px 13px', color: '#2d3748', fontSize: '0.62rem',
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
    borderBottom: '1px solid #1a1e28', textAlign: 'left', whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.02)' },
  td: { padding: '12px 13px', color: '#94a3b8', fontSize: '0.83rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  tfootRow: { borderTop: '1px solid #1e2530', background: 'rgba(255,255,255,0.02)' },

  /* chart */
  chartWrap: { display: 'flex', gap: 10 },
  yAxis: { display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: 46, flexShrink: 0, paddingBottom: 24 },
  yLabel: { color: '#2d3748', fontSize: '0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  chartBody: { flex: 1, position: 'relative', height: 200 },
  gridLine: { position: 'absolute', left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' },
  barsArea: { position: 'absolute', top: 0, left: 2, right: 2, bottom: 24, display: 'flex', gap: 4, alignItems: 'flex-end' },
  barCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1, height: '100%', cursor: 'pointer', transition: 'opacity 0.2s' },
  barTrack: { width: '100%', flex: 1, position: 'relative', borderRadius: 5, overflow: 'hidden', background: 'rgba(255,255,255,0.025)' },
  segment: { position: 'absolute', bottom: 0, left: 0, width: '100%', transition: 'height 0.8s cubic-bezier(.22,1,.36,1)', borderRadius: '3px 3px 0 0' },
  barLbl: { fontSize: '0.58rem', flexShrink: 0, transition: 'color 0.2s', fontVariantNumeric: 'tabular-nums' },

  /* legend */
  legend: { display: 'flex', flexWrap: 'wrap', gap: '6px 14px' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 5 },
  legendDot: { width: 8, height: 8, borderRadius: 2, flexShrink: 0 },
  legendLbl: { color: '#475569', fontSize: '0.68rem', fontWeight: 600 },

  /* day detail */
  dayDetail: { marginTop: 18, borderTop: '1px solid #1a1e28', paddingTop: 16 },
  dayDetailHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  dayDetailTitle: { color: '#94a3b8', fontSize: '0.8rem', fontWeight: 700 },
  dayClose: { background: 'none', border: 'none', color: '#334155', cursor: 'pointer', fontSize: '0.9rem' },
  dayGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 9 },
  dayCard: { display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: '10px 13px', overflow: 'hidden' },
  dayCardBar: c => ({ width: 3, height: 32, borderRadius: 2, background: c, flexShrink: 0 }),
  dayCardName: { color: '#475569', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 },
  dayCardVal:  { fontSize: '0.9rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  dayCardPct:  { color: '#334155', fontSize: '0.7rem', fontWeight: 700, marginLeft: 'auto' },

  /* tooltip */
  tooltip: {
    position: 'fixed', transform: 'translateX(-50%) translateY(-100%)',
    background: '#0f1115', border: '1px solid #1e2530',
    borderRadius: 10, padding: '10px 14px', zIndex: 9999,
    pointerEvents: 'none', whiteSpace: 'pre',
    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    lineHeight: 1.8, minWidth: 160,
  },
};

/* ─── SVG Line Chart — Kasa Bazlı ────────────────────────────────── */
const LineChart = ({ data, kasalar, yAxisMax, yLabels, ySteps, activeGun, setActiveGun, fmt, fmtShort, kasaRenk }) => {
  const W = 900, H = 200, PAD = { top: 12, right: 16, bottom: 28, left: 52 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const [hover, setHover] = React.useState(null);

  if (!data.length) return null;

  const xStep  = innerW / Math.max(data.length - 1, 1);
  const yScale = v => innerH - (v / yAxisMax) * innerH;

  // Toplam çizgisi noktaları
  const totalPts = data.map((g, i) =>
    `${PAD.left + i * xStep},${PAD.top + yScale(g.toplam)}`
  ).join(' ');

  const hoverDay = hover != null ? data.find(d => d.gun === hover) : null;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', overflow: 'visible', display: 'block' }}
      >
        <defs>
          <linearGradient id="grad-total" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f27127" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#f27127" stopOpacity="0" />
          </linearGradient>
          {kasalar.map(k => (
            <linearGradient key={k} id={`gk-${k}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={kasaRenk(k)} stopOpacity="0.08" />
              <stop offset="100%" stopColor={kasaRenk(k)} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {/* Grid lines + Y labels */}
        {yLabels.map((v, i) => {
          const y = PAD.top + (i / ySteps) * innerH;
          return (
            <g key={i}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              <text x={PAD.left - 6} y={y + 4} textAnchor="end"
                fill="#2d3748" fontSize="9" fontFamily="Plus Jakarta Sans, sans-serif">
                {fmtShort(v)}
              </text>
            </g>
          );
        })}

        {/* X labels */}
        {data.map((g, i) => (
          <text key={i}
            x={PAD.left + i * xStep} y={H - 6}
            textAnchor="middle"
            fill={activeGun === g.gun || hover === g.gun ? '#94a3b8' : '#2d3748'}
            fontSize="9" fontFamily="Plus Jakarta Sans, sans-serif">
            {g.gun}
          </text>
        ))}

        {/* Toplam alan dolgusu */}
        {data.length > 1 && (
          <polygon
            points={[
              `${PAD.left},${PAD.top + innerH}`,
              ...data.map((g, i) => `${PAD.left + i * xStep},${PAD.top + yScale(g.toplam)}`),
              `${PAD.left + (data.length - 1) * xStep},${PAD.top + innerH}`,
            ].join(' ')}
            fill="url(#grad-total)"
          />
        )}

        {/* Kasa çizgileri */}
        {kasalar.map(k => {
          const pts = data.map((g, i) => {
            const v = g.kasalar[k]?.yatirim ?? 0;
            return `${PAD.left + i * xStep},${PAD.top + yScale(v)}`;
          }).join(' ');
          const isHov = hover != null;
          return (
            <polyline key={k} points={pts} fill="none"
              stroke={kasaRenk(k)}
              strokeWidth={1.8}
              strokeLinecap="round" strokeLinejoin="round"
              opacity={isHov ? 0.55 : 0.8}
              style={{ transition: 'opacity 0.15s' }}
            />
          );
        })}

        {/* Toplam çizgisi */}
        {data.length > 1 && (
          <polyline points={totalPts} fill="none"
            stroke="#f27127" strokeWidth={2.5}
            strokeLinecap="round" strokeLinejoin="round"
            opacity={hover != null ? 0.5 : 1}
            style={{ transition: 'opacity 0.15s' }}
          />
        )}

        {/* Hover: dikey çizgi */}
        {hoverDay && (() => {
          const i = data.indexOf(hoverDay);
          const x = PAD.left + i * xStep;
          return (
            <line x1={x} y1={PAD.top} x2={x} y2={PAD.top + innerH}
              stroke="rgba(255,255,255,0.07)" strokeWidth="1" strokeDasharray="3,3" />
          );
        })()}

        {/* Hover: noktalar */}
        {hoverDay && (() => {
          const i = data.indexOf(hoverDay);
          return (
            <g>
              {kasalar.map(k => {
                const v = hoverDay.kasalar[k]?.yatirim ?? 0;
                if (!v) return null;
                return (
                  <circle key={k}
                    cx={PAD.left + i * xStep}
                    cy={PAD.top + yScale(v)}
                    r={3.5} fill={kasaRenk(k)} stroke="#07080b" strokeWidth="1.5"
                  />
                );
              })}
              {/* Toplam nokta */}
              <circle
                cx={PAD.left + i * xStep}
                cy={PAD.top + yScale(hoverDay.toplam)}
                r={5} fill="#f27127" stroke="#07080b" strokeWidth="2"
              />
            </g>
          );
        })()}

        {/* Invisible hover areas */}
        {data.map((g, i) => {
          const x = PAD.left + i * xStep;
          const hw = i === 0 ? xStep / 2 : (i === data.length - 1 ? xStep / 2 : xStep);
          const hx = i === 0 ? x : x - xStep / 2;
          return (
            <rect key={i} x={hx} y={PAD.top} width={hw} height={innerH}
              fill="transparent"
              onMouseEnter={() => setHover(g.gun)}
              onMouseLeave={() => setHover(null)}
              onClick={() => setActiveGun(activeGun === g.gun ? null : g.gun)}
              style={{ cursor: 'pointer' }}
            />
          );
        })}
      </svg>

      {/* Hover popup — sağ üst */}
      {hoverDay && (
        <div style={{
          position: 'absolute', top: 0, right: 0,
          background: 'rgba(10,12,16,0.97)', border: '1px solid #1e2530',
          borderRadius: 12, padding: '12px 16px', minWidth: 220,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', pointerEvents: 'none', zIndex: 10,
        }}>
          <div style={{ color: '#f27127', fontSize: '0.72rem', fontWeight: 800, marginBottom: 10, letterSpacing: '0.08em' }}>
            {hoverDay.gun}. GÜN
          </div>
          {/* Kasa kırılımları */}
          {kasalar
            .filter(k => hoverDay.kasalar[k]?.yatirim || hoverDay.kasalar[k]?.cekim)
            .sort((a, b) => (hoverDay.kasalar[b]?.yatirim ?? 0) - (hoverDay.kasalar[a]?.yatirim ?? 0))
            .map(k => {
              const yat = hoverDay.kasalar[k]?.yatirim ?? 0;
              const cek = hoverDay.kasalar[k]?.cekim   ?? 0;
              return (
                <div key={k} style={{ marginBottom: 7 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 2, background: kasaRenk(k), display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: '#64748b', fontSize: '0.67rem', fontWeight: 700, letterSpacing: '0.05em' }}>{k}</span>
                  </div>
                  <div style={{ paddingLeft: 13, display: 'flex', gap: 12 }}>
                    {yat > 0 && <span style={{ color: '#4ade80', fontSize: '0.7rem', fontVariantNumeric: 'tabular-nums' }}>↑ {fmt(yat)}</span>}
                    {cek > 0 && <span style={{ color: '#f87171', fontSize: '0.7rem', fontVariantNumeric: 'tabular-nums' }}>↓ {fmt(cek)}</span>}
                  </div>
                </div>
              );
            })}
          {/* Toplam */}
          <div style={{ borderTop: '1px solid #1e2530', marginTop: 8, paddingTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#475569', fontSize: '0.67rem' }}>Toplam Yatırım</span>
              <span style={{ color: '#4ade80', fontSize: '0.72rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmt(hoverDay.toplam)} ₺</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#475569', fontSize: '0.67rem' }}>Toplam Çekim</span>
              <span style={{ color: '#f87171', fontSize: '0.72rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmt(hoverDay.toplamCekim)} ₺</span>
            </div>
          </div>
        </div>
      )}

      {/* Seçili gün detay kartları */}
      {activeGun && (() => {
        const g = data.find(d => d.gun === activeGun);
        if (!g) return null;
        const sorted = kasalar
          .filter(k => g.kasalar[k]?.yatirim || g.kasalar[k]?.cekim)
          .sort((a, b) => (g.kasalar[b]?.yatirim ?? 0) - (g.kasalar[a]?.yatirim ?? 0));
        return (
          <div style={{ marginTop: 16, borderTop: '1px solid #1a1e28', paddingTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 700 }}>{g.gun}. Gün — Kasa Kırılımı</span>
              <button style={{ background: 'none', border: 'none', color: '#334155', cursor: 'pointer' }} onClick={() => setActiveGun(null)}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
              {sorted.map(k => {
                const yat = g.kasalar[k]?.yatirim ?? 0;
                const cek = g.kasalar[k]?.cekim   ?? 0;
                const pct = yat > 0 ? ((yat / g.toplam) * 100).toFixed(1) : '0.0';
                return (
                  <div key={k} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${kasaRenk(k)}20`, borderRadius: 10, padding: '10px 12px', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${pct}%`, height: 2, background: kasaRenk(k) }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: kasaRenk(k), flexShrink: 0 }} />
                      <span style={{ color: '#475569', fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k}</span>
                    </div>
                    <div style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>↑ {fmt(yat)}</div>
                    {cek > 0 && <div style={{ color: '#f87171', fontSize: '0.78rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>↓ {fmt(cek)}</div>}
                    <div style={{ color: '#1e293b', fontSize: '0.62rem', marginTop: 4 }}>{pct}% yatırım payı</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};


export default FinansDashboard;
